/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Sign-in: one card on a plain surface, the Pearl CRM lockup on the header
 * gradient, and nothing else to read. Drawn in the v2 look for every tenant:
 * nobody's UI version is known before sign-in, so App.js applies the v2
 * theme and the no-zoom body class on this route regardless.
 *
 * Calls signin() from auth/Auth.js and POST /forgot-password/.
 */

import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Link from "@mui/material/Link";
import Snackbar from "@mui/material/Snackbar";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Eye, EyeOff } from "lucide-react";

import { useAuth } from "auth/Auth";
import PearlLogoLoader from "components/PearlLogoLoader";
import pearlLogoBlue from "assets/images/brand/pearl-crm-logo-blue.png";
import loginBackground from "assets/images/brand/login-background.jpg";
import { V2_DISPLAY_FONT_FAMILY, v2 as t } from "styles/tokens/v2";
import { getApiErrorMessage } from "utils/apiErrors";

// The theme's contained button leaves the label dark; white on brand blue.
const primaryButtonSx = {
  bgcolor: t.primary,
  color: `${t.onPrimary} !important`,
  "&:hover": { bgcolor: t.primaryDark },
};

// Legal pages and the maker. Overridable per deployment, e.g. a regional site.
export const PRIVACY_URL = process.env.REACT_APP_PRIVACY_URL || "https://www.pearlcrm.com/privacy-policy";
export const TERMS_URL = process.env.REACT_APP_TERMS_URL || "https://www.pearlcrm.com/terms-and-conditions";
export const PSAG_URL = "https://psagtechnologies.com";

function ExternalLink({ href, children }) {
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      underline="hover"
      sx={{ color: t.text2, fontWeight: 600, fontSize: "inherit" }}
    >
      {children}
    </Link>
  );
}

export function formatLoginError(error) {
  if (error?.message === "Network Error") {
    return "Unable to reach the server. Please check your connection and try again.";
  }
  return getApiErrorMessage(
    error,
    "An error occurred during authentication. Please try again."
  );
}

function FieldLabel({ htmlFor, children, action }) {
  return (
    <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 0.75 }}>
      <Typography
        component="label"
        htmlFor={htmlFor}
        sx={{ fontSize: 13, fontWeight: 600, color: t.text2 }}
      >
        {children}
      </Typography>
      {action}
    </Box>
  );
}

export default function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  // Keep the query string: /oauth/consent?req=... is useless without it.
  const from = location.state?.from;
  const redirectPath = from?.pathname ? `${from.pathname}${from.search || ""}` : "/dashboard";

  const { signin, axiosInstance, isAuthenticated, isLoading, isAuthReady } = useAuth();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({ username: "", password: "" });
  const [errorMessage, setErrorMessage] = useState("");
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [resetOpen, setResetOpen] = useState(false);
  const [resetUsername, setResetUsername] = useState("");
  const [resetSaving, setResetSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    if (!isAuthReady || isLoading) return;
    if (isAuthenticated) {
      navigate(redirectPath, { replace: true });
      return;
    }
    setIsCheckingAuth(false);
  }, [isAuthReady, isLoading, isAuthenticated, navigate, redirectPath]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const errors = {
      username: username.trim() ? "" : "Username is required",
      password: password.trim() ? "" : "Password is required",
    };
    setFieldErrors(errors);
    setErrorMessage("");
    if (errors.username || errors.password) return;

    setIsSubmitting(true);
    try {
      const result = await signin(username, password, rememberMe);
      if (result?.redirecting) return;
      navigate(redirectPath, { replace: true });
    } catch (error) {
      console.error("Authentication failed:", error);
      setErrorMessage(formatLoginError(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const openReset = (e) => {
    e?.preventDefault?.();
    setResetUsername(username.trim());
    setResetOpen(true);
  };

  const closeReset = () => {
    setResetOpen(false);
    setResetUsername("");
  };

  const handleConfirmReset = async () => {
    setResetSaving(true);
    try {
      if (!resetUsername.includes("@")) {
        throw new Error("Invalid username format. Please include your schema (e.g., username@schema)");
      }
      const response = await axiosInstance.post("/forgot-password/", {
        username: resetUsername,
        reset_link: `${window.location.origin}/reset-password`,
      });
      closeReset();
      setSnackbar({
        open: true,
        message: `Password reset link has been sent to ${response.data.email}`,
        severity: "success",
      });
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = Array.isArray(detail)
        ? detail.map((item) => item.msg || JSON.stringify(item)).join(", ")
        : detail || error.message || "Failed to send reset password email";
      setSnackbar({ open: true, message, severity: "error" });
    } finally {
      setResetSaving(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: t.bg,
        backgroundImage: `url(${loginBackground})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 4,
      }}
    >
      {isCheckingAuth && (
        <PearlLogoLoader overlay message="Checking authentication..." size="lg" />
      )}

      <Box
        sx={{
          width: "100%",
          maxWidth: 400,
          bgcolor: t.surface,
          border: `1px solid ${t.border}`,
          borderRadius: "12px",
          boxShadow: t.shadowPop,
          overflow: "hidden",
        }}
      >
        <Box sx={{ px: 4, pt: 4, display: "flex", justifyContent: "center" }}>
          <Box
            component="img"
            src={pearlLogoBlue}
            alt="Pearl CRM"
            sx={{ height: 52, width: "auto", display: "block" }}
          />
        </Box>

        <Box component="form" noValidate onSubmit={handleSignIn} sx={{ px: 4, pt: 3, pb: 4 }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: V2_DISPLAY_FONT_FAMILY,
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: "-0.01em",
              color: t.text,
              mb: 0.5,
            }}
          >
            Sign in
          </Typography>
          <Typography sx={{ fontSize: 14, color: t.text3, mb: 3 }}>
            Use your username and workspace, for example jane.doe@acme.
          </Typography>

          {errorMessage && (
            <Alert severity="error" onClose={() => setErrorMessage("")} sx={{ mb: 2.5 }}>
              {errorMessage}
            </Alert>
          )}

          <FieldLabel htmlFor="signin-username">Username</FieldLabel>
          <TextField
            id="signin-username"
            fullWidth
            autoFocus
            autoComplete="username"
            placeholder="firstname.lastname@tenant"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              if (fieldErrors.username) setFieldErrors((prev) => ({ ...prev, username: "" }));
            }}
            error={Boolean(fieldErrors.username)}
            helperText={fieldErrors.username || " "}
            disabled={isSubmitting}
          />

          <FieldLabel
            htmlFor="signin-password"
            action={
              <Link
                component="button"
                type="button"
                onClick={openReset}
                underline="hover"
                sx={{ fontSize: 13, fontWeight: 600, color: t.link }}
              >
                Forgot password?
              </Link>
            }
          >
            Password
          </FieldLabel>
          <TextField
            id="signin-password"
            fullWidth
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (fieldErrors.password) setFieldErrors((prev) => ({ ...prev, password: "" }));
            }}
            error={Boolean(fieldErrors.password)}
            helperText={fieldErrors.password || " "}
            disabled={isSubmitting}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    edge="end"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
              />
            }
            label={<Typography sx={{ fontSize: 14, color: t.text2 }}>Remember me</Typography>}
            sx={{ ml: -0.75, mb: 2 }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={isSubmitting}
            sx={{ ...primaryButtonSx, height: 40, fontSize: 14, fontWeight: 600 }}
          >
            {isSubmitting ? "Signing in..." : "Sign in"}
          </Button>
        </Box>
      </Box>

      <Box
        component="footer"
        sx={{
          mt: 3,
          px: 2,
          py: 1,
          borderRadius: "8px",
          // A light veil so the links stay readable over any part of the picture.
          bgcolor: "rgba(255, 255, 255, 0.72)",
          textAlign: "center",
          fontSize: 12.5,
          color: t.text2,
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", flexWrap: "wrap" }}>
          <ExternalLink href={PRIVACY_URL}>Privacy Policy</ExternalLink>
          <span aria-hidden>·</span>
          <ExternalLink href={TERMS_URL}>Terms &amp; Conditions</ExternalLink>
        </Box>
        <Typography sx={{ fontSize: 12.5, color: t.text2, mt: 0.5 }}>
          © 2026 PearlCRM · A product by <ExternalLink href={PSAG_URL}>PSAG Technologies</ExternalLink>
        </Typography>
      </Box>

      <Dialog open={resetOpen} onClose={closeReset} fullWidth maxWidth="xs">
        <DialogTitle>Reset your password</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: t.text2, mb: 2 }}>
            Enter the same username@workspace you sign in with. We&apos;ll email a reset link
            to the address on your account.
          </Typography>
          <FieldLabel htmlFor="reset-username">Username</FieldLabel>
          <TextField
            id="reset-username"
            fullWidth
            autoFocus
            placeholder="you@workspace"
            value={resetUsername}
            onChange={(e) => setResetUsername(e.target.value)}
            disabled={resetSaving}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeReset} disabled={resetSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmReset}
            sx={primaryButtonSx}
            disabled={resetSaving || !resetUsername.trim()}
          >
            {resetSaving ? "Sending..." : "Send reset link"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
